import { gql } from "graphql-tag"

import { DEFAULTS } from "@/graphql/schemas/shared/utils/defaults"

const typeCart = gql`
    type LineItem {
        id: String
        productId: String
        productKey: String
        name: JSON
        productSlug: JSON
        productType: JSON
        variant: JSON
        price: JSON
        quantity: Int
        totalPrice: JSON
        discountedPricePerQuantity: [JSON]
        taxedPrice: JSON
        taxedPricePortions: [JSON]
        state:[JSON]
        taxRate: JSON
        perMethodTaxRate: [JSON]
        supplyChannel: JSON
        distributionChannel: JSON
        priceMode: String
        lineItemMode: String
        inventoryMode: String
        shippingDetails: JSON
        addedAt: DateTime
        custom: JSON
        lastModifiedAt: DateTime
    }

    type CustomLineItem {
        id: ID
        name: JSON
        money: JSON
        taxedPrice: JSON
        totalPrice: JSON
        slug: String
        quantity: Int
        state: [JSON]
        taxCategory: JSON
        taxRate: JSON
        discountedPricePerQuantity: [JSON]
        shippingDetails: JSON
        ItemShippingDetails: JSON
        priceMode: String
        custom: JSON
        article: Article
        listing: CustomLineItemListing
        amountBilling: Float
        sellableInDecimals: Boolean
        minSellingPrice: MinSellingPrice
        chargenData: [Chargen]
        listPrices: [ListPrice]
        purchasePrice: Float
        grossSellingPrice: Float
        pastOrders: [PastArticleOrders]
        pastReservations: [PastArticleOrders]
        avgDb: Float
    }

    type PastArticleOrderItems {
        sku: String
        price: Float
        discount: Float
    }

    type PastArticleOrders {
        branchNumber: String
        orderNumberLong: String
        customerNumber: String
        orderDate: String
        validTill: String
        orderNumberShort: String
        items: [PastArticleOrderItems]
    }

    type MinSellingPrice {
        type: String
        currencyCode: String
        centAmount: Int
        fractionDigits: Int
        preciseAmount: Int
    }

    type CustomLineItemListing {
        availability: String
        availableQuantity: String
        quantityOnStock: String
        reservedQuantity: String
    }

    type Cart {
        id: String
        version: Int
        key: String
        customer: Customer

        customerId: ID
        customerEmail: EmailAddress
        customerGroup: JSON

        anonymousId: String

        businessUnit: JSON
        store: JSON

        lineItems: [LineItem]
        customLineItems: [CustomLineItem]
        totalLineItemQuantity: Int

        totalPrice: JSON
        taxedPrice: JSON
        taxedShippingPrice: JSON
        taxMode: String
        taxRoundingMode: String
        taxCalculationMode: String

        inventoryMode: String

        cartState: String

        billingAddress: JSON
        shippingAddress: JSON

        shippingMode: String
        shippingKey: String
        shippingInfo: JSON
        shippingRateInput: JSON
        shippingCustomFields: JSON
        shipping: [JSONObject]
        itemShippingAddresses: [JSONObject]

        discountCodes: [JSONObject]
        directDiscounts: [JSONObject]
        refusedGifts: [JSONObject]

        paymentInfo: JSON

        country: String
        locale: Locale
        origin: String
        deleteDaysAfterLastModification: Int
        custom: JSON

        createdAt: DateTime
        createdBy: JSON
        lastModifiedAt: DateTime
        lastModifiedBy: JSON

        sokoDiscountValue: Float
        sokoDiscountPercent: Int
        sokoPrice: Float
        reservationNumber: String
        picklistNumber: String
        picklistOrderNumber2: String
        picklistOrderType: String
        groupBillingKey: String
        paymentTill: String
    }

    type Chargen {
        chargenNo: String
        availableQuantity: Int
    }

    type Carts {
        limit: Int
        offset: Int
        count: Int
        total: Int

        items: [Cart]
    }

    type CartSkus {
        skus: [String]
    }

    type ScannedCart {
      cart: Cart
      scannedItems: Int
    }

    type Query {
        customerCart(customerId: ID, customerNumber: String, lang: String = "", cdn: Boolean = true, loggedInBranch: String, regionSanityId: String, enriched: Boolean = false, checkout: Boolean = false): Cart

        paginatedCustomerCart(
            customerId: ID,
            customerNumber: String,
            offset: Int,
            limit: Int,
            lang: String = "",
            cdn: Boolean = true,
            loggedInBranch: String,
            regionSanityId: String
        ): Cart

        customerCarts(
            customerId: ID
            customerNumber: String
            onlyActive: Boolean = false

            offset: Int = ${DEFAULTS.QUERY.OFFSET}
            limit: Int = ${DEFAULTS.QUERY.LIMIT}
            sort: Sort
        ): Carts

        customerCartSkus(customerId: ID, customerNumber: String, lang: String = "", cdn: Boolean = true, loggedInBranch: String): CartSkus
        userCarts(limit: Int, offset: Int, customerId: ID): Carts
    }

    input LineItemDraft {
        productId: String
        variantId: Int
        sku: String
        quantity: Int
        custom: JSONObject
    }

    input CustomLineItemDraft {
        productKey: String!
        sku: String!
        quantity: Float!
        colorId: String
        additionalInformation: String
        additionalComment: String
        allowMultiple: Boolean = false
    }

    input LineItemIdAndQuantity {
        id: ID
        slug: String
        quantity: Float
    }

    input LineItemIdAndConfigValues {
        id: ID
        slug: String
        discountPercent: Float
        newPrice: Float
        sendTechnicalManual: Boolean
        sendProductText: Boolean
        sendCertificateWithOffer: Boolean
        additionalInformation: String
        additionalComment: String
        position: Int
    }

    input CustomCartValues {
        freightSurcharge: Float
    }

    type Mutation {
        createCustomerCart(
            customerId: ID
            customerKey: String
            cartKey: String
            currency: String = "EUR"
            storeKey: String
            country: String
            origin: String = "Merchant"
        ): Cart

        deleteCustomerCart(
            customerId: ID
            customerKey: String
            cartId: ID
            cartKey: String
        ): Cart

        deleteAllCustomerCarts(
            customerId: ID
            customerKey: String,
        ): [Cart]

        addLineItems(
            cartId: ID
            cartKey: String
            storeKey: String
            items: [LineItemDraft]
        ): Cart

        removeLineItems(
            cartId: ID
            cartKey: String
            items:[LineItemIdAndQuantity]
            allItems: Boolean = false
        ): Cart

        addCustomLineItems(
            lang: String = ""
            cdn: Boolean = true
            loggedInBranch: String
            cartId: ID
            cartKey: String
            customerId: String
            storeKey: String
            items: [CustomLineItemDraft]
        ): Cart

        removeCustomLineItems(
            lang: String = ""
            cdn: Boolean = true
            loggedInBranch: String
            cartId: ID
            cartKey: String
            items:[LineItemIdAndQuantity]
            allItems: Boolean = false
        ): Cart

        updateCustomLineItemsConfiguration(
            lang: String = ""
            cdn: Boolean = true
            loggedInBranch: String
            cartId: ID
            cartKey: String
            items: [LineItemIdAndConfigValues]
        ): Cart

        updateLineItemsByQuantity(
            lang: String = ""
            cdn: Boolean = true
            loggedInBranch: String
            cartId: ID
            cartKey: String
            items:[LineItemIdAndQuantity]
        ): Cart

        updateCustomerCart(
            lang: String = ""
            cdn: Boolean = true
            loggedInBranch: String
            customerId: String
            customerNumber: String
            values: CustomCartValues
        ): Cart

        transferOrderItemsToCart(
            lang: String = ""
            cdn: Boolean = true
            loggedInBranch: String
            customerId: ID
            customerNumber: String
            orderNumber: String
            withPrice: Boolean = false
        ): Cart

        transferOrderAddressToCart(
            loggedInBranch: String
            customerId: ID
            customerNumber: String
            orderNumber: String
        ): Cart

        syncCustomerCartWithStore(
            lang: String = ""
            cdn: Boolean = true
            customerNumber: String!
            branchId: String!
            ignoreReservation: Boolean = false
        ): Cart

        addScanCustomLineItems(
          lang: String = ""
          cdn: Boolean = true
          customerId: String!
          eanCode: String
          sku: String
          branchId: String!
        ): ScannedCart

        resetCartShippingAddress(
          customerId: ID
          customerNumber: String
        ): Cart

        copyPicklistToCart(
            customerId: ID
            customerNumber: String
            orderNumber: String
            loggedInBranch: String
        ): Cart

        clearPicklistEditingMode(
            customerId: ID
            customerNumber: String
            cartId: ID
        ): Cart
    }
`

export { typeCart }

export default typeCart
